/**
 * Subscription engine, phase 1: the quote, the first invoice, and the
 * invoice pay link.
 *
 * Nothing here asks the customer to choose a package or a seat count: the
 * quote is what they use at its price band, and a trial ends by raising its
 * first invoice. The one choice is how to pay: each term or month, or a year
 * up front at the price book's yearly discount.
 */
import { api } from '@lipaflow/shared'

export interface BillingQuote {
  count: number
  unit_label: string
  band: string | null
  rate: number | null
  contract: boolean
  /** What this quote is for: the sector's own cycle, or "yearly". */
  cycle: string
  /** The sector's own cycle ("termly", "monthly") and what can be chosen. */
  base_cycle: string
  cycles: string[]
  /** Periods covered: 1, or a year's worth up front. */
  periods: number
  period_start: string
  period_end: string
  days_covered: number
  period_days: number
  amount: number | null
  currency: string
  needs_contract: boolean
  /** How the amount is worked out: one line per price band, then discounts,
   * the minimum charge and tax. Graduated bands make `count × rate` wrong. */
  lines?: BillingQuoteLine[]
}

export interface BillingQuoteLine {
  kind: 'base' | 'proration' | 'addon' | 'discount' | 'voucher' | 'adjustment' | 'tax'
  description: string
  amount: number
}

export interface PublicInvoice {
  organisation: string
  number: string
  description: string
  status: string
  currency: string
  quantity: number
  unit_rate: number
  subtotal: number
  discount: number
  discount_description: string | null
  amount: number
  paid_amount: number
  remaining_amount: number
  due_date: string | null
  service_period_start: string | null
  service_period_end: string | null
  paid_at: string | null
  payment: { status: string; reference: string | null; amount: number } | null
  receipt_reference: string | null
}

/** The plan, what's paid, the next renewal and its countdown (``renewal_summary``). */
export interface RenewalSummary {
  /** How the period running now was bought ("termly", "monthly", "yearly"). */
  current_cycle?: string
  /** Days ahead each plan renews, e.g. { termly: 14, yearly: 30 }. */
  notices?: Record<string, number>
  licences_held: number | null
  licences_in_use: number
  plan: string
  plans: string[]
  period: string
  paid_until: string | null
  covered_until: string | null
  ends_at: string | null
  days_left: number | null
  notice_days: number
  renews_at: string | null
  showing: boolean
  cancelling: boolean
  invoice: {
    id: string
    number: string
    amount: number
    remaining: number
    status: string
    cycle: string | null
    seats: number
    starts: string | null
    ends: string | null
    due_date: string | null
  } | null
}

export const billingService = {
  /** How to pay from the next renewal: each period, or "yearly". */
  async choosePlan(plan: string): Promise<RenewalSummary> {
    return (await api.put<RenewalSummary>('/subscriptions/plan', { plan })).data
  },

  /** Stop renewing; what's paid for stays until it ends. */
  async cancelRenewal(): Promise<RenewalSummary> {
    return (await api.post<RenewalSummary>('/subscriptions/cancel')).data
  },

  async resumeRenewal(): Promise<RenewalSummary> {
    return (await api.post<RenewalSummary>('/subscriptions/resume')).data
  },

  async getQuote(cycle?: string): Promise<BillingQuote> {
    return (await api.get<BillingQuote>('/subscriptions/quote', { params: cycle ? { cycle } : undefined })).data
  },

  /** Raise the first invoice now, in the chosen cycle (idempotent). Asking for
   *  the other cycle replaces an unpaid first invoice. */
  async raiseFirstInvoice(cycle?: string): Promise<{ invoice_id: string; number: string; amount: number; cycle: string }> {
    return (await api.post('/subscriptions/first-invoice', cycle ? { cycle } : {})).data
  },

  /** Pay one of the organisation's own LipaFlow invoices by card or M-PESA:
   *  returns where to send the browser (same tab, Rule 4). */
  async payInvoice(invoiceId: string, amount?: number): Promise<{ authorization_url: string }> {
    return (await api.post(`/subscriptions/invoices/${invoiceId}/pay`, amount ? { amount } : undefined)).data
  },

  /** A fresh no-login link for one invoice; earlier links stop working. */
  async createPayLink(invoiceId: string): Promise<string> {
    return (await api.post<{ url: string }>(`/subscriptions/invoices/${invoiceId}/pay-link`)).data.url
  },

  // --- The pay link's public side (no sign-in) ---------------------------

  async resolveInvoice(token: string): Promise<PublicInvoice> {
    return (await api.post<PublicInvoice>('/public/invoice-pay/resolve', { token })).data
  },

  async startCheckout(token: string): Promise<{ authorization_url: string }> {
    return (await api.post('/public/invoice-pay/checkout', { token })).data
  },

  async checkPayment(token: string): Promise<PublicInvoice> {
    return (await api.post<PublicInvoice>('/public/invoice-pay/status', { token })).data
  },
}

export function formatMoney(amount: number | null | undefined, currency = 'KES'): string {
  if (amount === null || amount === undefined) return '—'
  return `${currency} ${Math.round(amount).toLocaleString('en-KE')}`
}

export function apiErrorDetail(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}
